/** Client for the sk-hire v2 match funnel + actions/kanban. */
import axiosInstance from '../../api/axiosInstance';
import {
  HireMatch_Requisition_MatchRuns_Url,
  HireMatch_MatchRun_Url,
  HireMatch_Requisition_LatestRun_Url,
  HireMatch_MatchRun_Feedback_Url,
  HireMatch_MatchRun_FeedbackItem_Url,
  HireMatch_MatchRun_GoodMatch_Url,
  HireMatch_MatchRun_FalsePositive_Url,
  HireMatch_Candidate_JobMatches_Url,
  HireMatch_Requisition_Pipeline_Url,
  HireMatch_Exclusion_Url,
  HireMatch_Actions_Url,
} from '../../api/endpoints';
import type {
  MatchRunSummary, MatchRunDetail, JobMatchesResponse, Pipeline, RecordActionInput, RecordActionResult,
  MatchFeedbackItem, FeedbackVerdict, ExclusionResult,
} from '../../schemas/hire/matchV2';

/**
 * Run a match. `mode: 'incremental'` scores ONLY the candidates the last run
 * never saw and merges them into its stored ranking — nothing already judged is
 * re-scored, and the AI rerank step is skipped, so it costs nothing. Use it when
 * candidates were added; a job edit needs a full run, because the criteria moved
 * and every stored score is invalid.
 */
export async function runMatch(
  requisitionId: string, mode?: 'incremental',
): Promise<MatchRunDetail> {
  const { data } = await axiosInstance.post<MatchRunDetail>(
    HireMatch_Requisition_MatchRuns_Url(requisitionId),
    undefined,
    mode ? { params: { mode } } : undefined,
  );
  return data;
}

/** Why the stored ranking may no longer stand. */
export interface RunStaleness {
  fresh: boolean;
  jobChanged: boolean;
  ranAgainstRevision: number | null;
  currentRevision: number;
  /** Candidates this job's run already judged, whose profile has since changed —
   *  job-relevant, because the stored verdict no longer fits the profile. */
  updatedCandidateIds: string[];
  /** Candidates this run never saw. Relevance is unknown until they are scored,
   *  so they are counted separately rather than reported on every job as an
   *  "updated candidate". */
  newCandidateIds: string[];
}

/**
 * The newest run for a job plus whether it is still current — read-only, costs
 * nothing. The job screen opens with this instead of POSTing a fresh run, so an
 * unchanged job never spends rerank tokens to reproduce the same list.
 */
export async function getLatestRun(
  requisitionId: string,
): Promise<{ run: MatchRunDetail | null; staleness: RunStaleness }> {
  const { data } = await axiosInstance.get<{ run: MatchRunDetail | null; staleness: RunStaleness }>(
    HireMatch_Requisition_LatestRun_Url(requisitionId),
  );
  return data;
}

export async function listRuns(requisitionId: string): Promise<MatchRunSummary[]> {
  const { data } = await axiosInstance.get<MatchRunSummary[]>(HireMatch_Requisition_MatchRuns_Url(requisitionId));
  return Array.isArray(data) ? data : [];
}

export async function getRun(runId: string): Promise<MatchRunDetail> {
  const { data } = await axiosInstance.get<MatchRunDetail>(HireMatch_MatchRun_Url(runId));
  return data;
}

export async function jobMatches(candidateId: string): Promise<JobMatchesResponse> {
  const { data } = await axiosInstance.get<JobMatchesResponse>(HireMatch_Candidate_JobMatches_Url(candidateId));
  return data;
}

export async function getPipeline(requisitionId: string): Promise<Pipeline> {
  const { data } = await axiosInstance.get<Pipeline>(HireMatch_Requisition_Pipeline_Url(requisitionId));
  return data;
}

/**
 * Record a pipeline move.
 *
 * Returns whether the job closed as a result: when the last opening is filled the
 * server closes the requisition, and the recruiter needs to hear that from the
 * action they just took rather than notice later that the job left their active
 * list. `openingsCount` is returned so the message can say how many seats were
 * filled.
 */
export async function recordAction(input: RecordActionInput): Promise<RecordActionResult> {
  const { data } = await axiosInstance.post<RecordActionResult>(HireMatch_Actions_Url, input);
  return data ?? { ok: true };
}

/* ── Match feedback (analytics-only) ──────────────────────────────────── */

export async function listMyFeedback(runId: string): Promise<MatchFeedbackItem[]> {
  const { data } = await axiosInstance.get<MatchFeedbackItem[]>(HireMatch_MatchRun_Feedback_Url(runId));
  return Array.isArray(data) ? data : [];
}

export async function recordFeedback(runId: string, candidateId: string, verdict: FeedbackVerdict): Promise<void> {
  await axiosInstance.post(HireMatch_MatchRun_Feedback_Url(runId), { candidateId, verdict });
}

/** Dedicated verdict endpoints (thin wrappers; same idempotent write). */
export async function markGoodMatch(runId: string, candidateId: string): Promise<void> {
  await axiosInstance.post(HireMatch_MatchRun_GoodMatch_Url(runId), { candidateId });
}

export async function markFalsePositive(runId: string, candidateId: string): Promise<void> {
  await axiosInstance.post(HireMatch_MatchRun_FalsePositive_Url(runId), { candidateId });
}

export async function clearFeedback(runId: string, candidateId: string): Promise<void> {
  await axiosInstance.delete(HireMatch_MatchRun_FeedbackItem_Url(runId, candidateId));
}

/* ── Exclusion lookup — "Why not matching?" ───────────────────────────── */

export async function exclusionLookup(requisitionId: string, candidateId: string): Promise<ExclusionResult> {
  const { data } = await axiosInstance.get<ExclusionResult>(HireMatch_Exclusion_Url(requisitionId, candidateId));
  return data;
}
